/**
 * Menu navigation for the DOM UI (keyboard + gamepad + mouse/touch): the Preact hooks and DOM focus handling over
 * the platform-neutral router of @hf/client-core `ui-model/menu-router.ts` (layer stack, top-most dispatch,
 * `inputStyle`, arming, grid routing — shared with the native shell).
 *
 * - The InputManager already handles keyboards, every gamepad (D-pad/stick with auto-repeat, A/B/Start) and the
 *   touch pause button, and it ignores keys typed into text fields. The web router adds a capture-phase keydown guard
 *   so Enter/Space on a focused <button> does not fire BOTH the browser click and the menu 'confirm', and
 *   hover-follows-focus for the mouse.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import type { InputManager } from '@hf/client-core/input/types';
import {
  createArming,
  gridAction,
  hoverArmed,
  LAYER,
  notePointerMove,
  registerMenuLayer,
  routeMenuInput,
  blockedWhileArming,
  inputStyle,
  UI_SOURCE,
  type Arming,
  type GridSpec,
  type MenuHandler,
  type MenuSource,
} from '@hf/client-core/ui-model/menu-router';

export {
  activeLayerCount,
  ARM_MS,
  ARM_QUIET_MS,
  blockedWhileArming,
  dispatchMenu,
  hoverArmed,
  inputStyle,
  LAYER,
  notePointerMove,
  registerMenuLayer,
  stepIndex,
  toMenuSource,
  topLayerPriority,
  UI_SOURCE,
  type MenuHandler,
  type MenuSource,
} from '@hf/client-core/ui-model/menu-router';

/**
 * Register a menu layer while `active`. Overlays use a higher `priority` (LAYER.OVERLAY) than screens.
 * The handler may change every render; the layer keeps the latest one.
 */
export const useMenuLayer = (handler: MenuHandler, active = true, priority: number = LAYER.SCREEN): void => {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => (active ? registerMenuLayer(ref, priority) : undefined), [active, priority]);
};

// ── Router ────────────────────────────────────────────────────────────────────────────────────────────────

const ACTIVATION_KEYS = new Set(['Enter', 'NumpadEnter', 'Space']);

const isEditable = (t: EventTarget | null): boolean => {
  const el = t as HTMLElement | null;
  if (!el || typeof el.tagName !== 'string') return false;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable) return true;
  if (tag !== 'INPUT') return false;
  const type = ((el as HTMLInputElement).type || 'text').toLowerCase();
  return !['button', 'checkbox', 'radio', 'range', 'submit', 'reset'].includes(type);
};

/** Is `el` (or its activatable ancestor) the router's focused grid item? */
const isRouterItem = (el: HTMLElement): boolean => !!el.closest?.('[data-focused="true"]');

/** Install the router (called once by <App>). Returns a disposer. */
export const installMenuRouter = (input: InputManager): (() => void) => {
  const disposers: (() => void)[] = [];
  /**
   * Set when Enter/Space activates a control the user TABBED to that is not the router's focused item (the Shell
   * ◀ back button, the HUD pause button…): the browser's own click runs, so the keyboard 'confirm' that the
   * InputManager emits for the same keydown must not ALSO confirm the grid's item (that started Exhibition when
   * Enter was pressed on BACK). Cleared after the keydown has been dispatched.
   */
  let swallowConfirm = false;
  /** The element that received focus through Tab / Shift+Tab (null after mouse focus or programmatic focus). */
  let tabFocused: EventTarget | null = null;
  let tabbing = false;
  disposers.push(
    routeMenuInput(input, (action, source) => {
      if (swallowConfirm && action === 'confirm' && source.kind === 'kb') {
        swallowConfirm = false;
        return true;
      }
      return false;
    }),
  );
  if (typeof window !== 'undefined') {
    const capture = (e: KeyboardEvent): void => {
      if (inputStyle.value !== 'kb') inputStyle.value = 'kb';
      if (e.key === 'Tab' || e.code === 'Tab') {
        tabbing = true;
        setTimeout(() => (tabbing = false), 0);
        return;
      }
      const el = e.target as HTMLElement | null;
      if (
        !ACTIVATION_KEYS.has(e.code) ||
        !el ||
        isEditable(el) ||
        !el.closest?.('button, [role="button"], a')
      )
        return;
      if (el === tabFocused && !isRouterItem(el)) {
        // A tabbed-to control outside the router's focus: let its native click run, and drop the menu 'confirm'.
        swallowConfirm = true;
        setTimeout(() => (swallowConfirm = false), 0);
        return;
      }
      // The router's 'confirm' activates the focused item; suppress the duplicate synthetic click.
      e.preventDefault();
    };
    const focusIn = (e: FocusEvent): void => {
      tabFocused = tabbing ? e.target : null;
    };
    const pointer = (e: PointerEvent): void => {
      inputStyle.value = e.pointerType === 'touch' ? 'touch' : 'kb';
      tabFocused = null;
    };
    const move = (e: PointerEvent): void => notePointerMove(e.clientX, e.clientY);
    window.addEventListener('keydown', capture, true);
    window.addEventListener('focusin', focusIn, true);
    window.addEventListener('pointerdown', pointer, true);
    window.addEventListener('pointermove', move, true);
    disposers.push(() => {
      window.removeEventListener('keydown', capture, true);
      window.removeEventListener('focusin', focusIn, true);
      window.removeEventListener('pointerdown', pointer, true);
      window.removeEventListener('pointermove', move, true);
    });
  }
  return () => {
    for (const d of disposers.splice(0)) d();
  };
};

// ── Arming (screens that appear mid-play) ─────────────────────────────────────────────────────────────────

const clockMs = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/**
 * Arming window for a screen or modal that can appear WHILE the player is mashing play keys (Results at the final
 * whistle, the CONNECTION LOST modal mid-play): J / Space / pad A are both "dive" and menu "confirm", so one stray
 * press used to fire REMATCH (or dismiss the modal) before the player saw anything. `ready()` is false for `ms`
 * after `key` last changed (mount, modal opening); a press blocked meanwhile (`poke()`) keeps it false until no
 * press came for ARM_QUIET_MS (core `createArming`). Menu presses are press EDGES (no key repeat): a key merely
 * held from play never confirms. `armed` re-renders the caller (styling).
 */
export const useArming = (
  ms: number,
  key: unknown = 0,
): { armed: boolean; ready(): boolean; poke(): void } => {
  const msRef = useRef(ms);
  msRef.current = ms;
  const arm = useRef<Arming | null>(null);
  if (!arm.current) arm.current = createArming(() => msRef.current, clockMs);
  const lastKey = useRef(key);
  if (lastKey.current !== key) {
    lastKey.current = key;
    arm.current.restart();
  }
  const [armed, setArmed] = useState(ms <= 0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const schedule = (): void => {
    if (timer.current) clearTimeout(timer.current);
    const left = arm.current!.readyAt() - clockMs();
    if (left <= 0) {
      timer.current = null;
      setArmed(true);
      return;
    }
    setArmed(false);
    timer.current = setTimeout(schedule, left);
  };
  useEffect(() => {
    if (ms <= 0) return;
    schedule();
    return () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [ms, key]);
  return {
    armed,
    ready: () => arm.current!.ready(),
    poke: () => {
      if (ms <= 0) return;
      arm.current!.poke();
      schedule();
    },
  };
};

// ── Focus lists / grids ───────────────────────────────────────────────────────────────────────────────────

export interface FocusGridOptions extends GridSpec {
  initial?: number;
  active?: boolean;
  priority?: number;
  onMove?(i: number): void;
  /**
   * Ignore device confirm / back / pause for this long after mount (`useArming`): screens that appear while the
   * player may be mashing play keys. Mouse clicks and directions are never blocked.
   */
  armMs?: number;
}

export interface FocusGrid {
  index: number;
  /** The arming window (`armMs`) is over (always true without one). */
  armed: boolean;
  setIndex(i: number): void;
  /** Spread onto each item: focus ring attribute, hover focus, click confirm, DOM focus for screen readers. */
  item(i: number): {
    ref: (el: HTMLElement | null) => void;
    'data-focused': boolean;
    onMouseEnter: () => void;
    onMouseMove: () => void;
    onClick: (e: MouseEvent) => void;
    tabIndex: number;
  };
}

export const useFocusGrid = (o: FocusGridOptions): FocusGrid => {
  const [index, setIndexState] = useState(() =>
    Math.min(Math.max(0, o.initial ?? 0), Math.max(0, o.count - 1)),
  );
  const els = useRef<(HTMLElement | null)[]>([]);
  const clamp = (i: number): number => Math.min(Math.max(0, i), Math.max(0, o.count - 1));
  const cur = clamp(index);
  const active = o.active ?? true;
  const arming = useArming(o.armMs ?? 0);

  const setIndex = (i: number): void => {
    const n = clamp(i);
    if (n !== cur) o.onMove?.(n);
    setIndexState(n);
  };

  useMenuLayer(
    (a, source) => {
      if (source.kind !== 'ui' && !arming.ready()) {
        // Still mashing (any device press, directions included, keeps the window shut until the keys go quiet).
        arming.poke();
        if (blockedWhileArming(a, source)) return;
      }
      gridAction(o, cur, a, source, setIndex);
    },
    active,
    o.priority ?? LAYER.SCREEN,
  );

  // Keep DOM focus on the focused item (screen readers, visible focus for keyboard users) without scroll jumps.
  useEffect(() => {
    if (!active || typeof document === 'undefined') return;
    const el = els.current[cur];
    if (el && typeof el.focus === 'function' && document.activeElement !== el) {
      const focused = document.activeElement as HTMLElement | null;
      if (focused && isEditable(focused)) return;
      el.focus({ preventScroll: true });
      el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    }
  }, [cur, active]);

  return {
    index: cur,
    armed: arming.armed,
    setIndex,
    item: (i) => ({
      ref: (el) => {
        els.current[i] = el;
      },
      'data-focused': active && i === cur,
      onMouseEnter: () => {
        // Only a pointer that actually moved takes focus (not one resting where a new screen/overlay appeared).
        if (hoverArmed() && !o.isDisabled?.(i) && i !== cur) setIndex(i);
      },
      // A cursor that was resting on the item when it appeared gets no new mouseenter: follow its first real move.
      onMouseMove: () => {
        if (hoverArmed() && !o.isDisabled?.(i) && i !== cur) setIndex(i);
      },
      onClick: (e) => {
        e.preventDefault();
        if (o.isDisabled?.(i)) return;
        setIndex(i);
        o.onConfirm?.(i, UI_SOURCE);
      },
      tabIndex: i === cur ? 0 : -1,
    }),
  };
};
